import React, { useState, useRef, useEffect, useCallback } from 'react';
import { generateCharacterPortraitWithGemini } from '../services/geminiService';
import { editImage, generateElementImage } from '../services/aiService';
import { LoadingSpinner } from './icons/LoadingSpinner';
import { CloseIcon } from './icons/CloseIcon';
import { MagicWandIcon } from './icons/MagicWandIcon';
import { PlusIcon } from './icons/PlusIcon';
import { TrashIcon } from './icons/TrashIcon';
import { CropIcon } from './icons/CropIcon';
import { UndoIcon } from './icons/UndoIcon';
import { RedoIcon } from './icons/RedoIcon';
import { PencilIcon } from './icons/PencilIcon';
import { CREDIT_COSTS } from '../types';

export const CharacterPortraitModal = ({ character, projectData, onClose, onSave, onDeductCredits, onTrackCreditsUsed, onSaveMedia }) => {
    // History state for undo/redo
    const [history, setHistory] = useState(character.image ? [character.image] : []);
    const [historyIndex, setHistoryIndex] = useState(character.image ? 0 : -1);
    const generatedImage = history[historyIndex] ?? null;
    
    const [referenceImages, setReferenceImages] = useState(character.referenceImages || []);
    const [costumeImages, setCostumeImages] = useState(character.costumeAndAccessoryImages || []);
    const [name, setName] = useState(character.name);
    const [prompt, setPrompt] = useState(character.description || '');
    const [editPrompt, setEditPrompt] = useState('');
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState(null);
    const [makeupLevel, setMakeupLevel] = useState(character.makeupLevel || 'natural');
    
    // Determine initial mode based on whether character has an image
    const hasExistingImageOnLoad = !!(character.image || character.portraitImage);
    const [mode, setMode] = useState(hasExistingImageOnLoad ? 'view' : 'generate');
    
    console.log('🎨 CharacterPortraitModal opened:', {
        characterName: character.name,
        hasImage: !!character.image,
        hasPortraitImage: !!character.portraitImage,
        category: character.category,
        initialMode: hasExistingImageOnLoad ? 'view' : 'generate'
    });
    
    // Edit mode states
    const [editTab, setEditTab] = useState('ai'); // 'ai', 'basic', 'filters'
    const [maskingEnabled, setMaskingEnabled] = useState(false);
    const [brushSize, setBrushSize] = useState(30);
    const [drawingMode, setDrawingMode] = useState('brush'); // 'brush' or 'eraser'
    const [isDrawing, setIsDrawing] = useState(false);
    
    // Crop state
    const [crop, setCrop] = useState(null);
    const [dragHandle, setDragHandle] = useState(null);
    const [dragStart, setDragStart] = useState(null);
    
    // Filter states
    const [brightness, setBrightness] = useState(100);
    const [contrast, setContrast] = useState(100);
    const [saturation, setSaturation] = useState(100);
    const [rotation, setRotation] = useState(0);
    const [flipH, setFlipH] = useState(false);
    const [flipV, setFlipV] = useState(false);
    
    // Refs
    const likenessFileInputRef = useRef(null);
    const costumeFileInputRef = useRef(null);
    const uploadNewImageRef = useRef(null);
    const editReferenceInputRef = useRef(null);
    const imageRef = useRef(null);
    const drawingCanvasRef = useRef(null);
    const lastPoint = useRef(null);
    
    // Determine item type
    const isElement = character.category === 'element';
    const isNarrator = character.category === 'narrator';
    const hasExistingImage = !!(character.image || character.portraitImage);
    
    // Get aspect ratio from project data
    const projectAspectRatio = projectData?.aspectRatio || '16:9';
    
    // Debug logging - only runs when key values change (not on every render)
    useEffect(() => {
        console.log('🖼️ Portrait Modal - Project Aspect Ratio:', projectAspectRatio, 'from projectData:', projectData?.aspectRatio);
        console.log('🖼️ Item category:', character.category, 'isElement:', isElement, 'hasExistingImage:', hasExistingImage);
    }, [projectAspectRatio, projectData?.aspectRatio, character.category, isElement, hasExistingImage]);

    // Add new image to history
    const addNewImageState = useCallback((newImage) => {
        const newHistory = history.slice(0, historyIndex + 1);
        newHistory.push(newImage);
        setHistory(newHistory);
        setHistoryIndex(newHistory.length - 1);
    }, [history, historyIndex]);

    // Undo/Redo handlers
    const handleUndo = () => {
        if (historyIndex > 0) {
            setHistoryIndex(historyIndex - 1);
        }
    };

    const handleRedo = () => {
        if (historyIndex < history.length - 1) {
            setHistoryIndex(historyIndex + 1);
        }
    };

    // File handling
    const handleFileChange = (e, setter) => {
        const files = e.target.files;
        if (!files) return;

        Array.from(files).forEach(file => {
            const reader = new FileReader();
            reader.onloadend = () => {
                setter(prev => [...prev, reader.result]);
            };
            reader.readAsDataURL(file);
        });
    };
    
    const handleRemoveReference = (index, setter) => {
        setter(prev => prev.filter((_, i) => i !== index));
    };

    // Upload new image to replace existing
    const handleUploadNewImage = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        
        const reader = new FileReader();
        reader.onloadend = () => {
            addNewImageState(reader.result);
            setMode('view');
        };
        reader.readAsDataURL(file);
    };

    // Canvas resize handler
    const handleCanvasResize = useCallback(() => {
        const image = imageRef.current;
        const canvas = drawingCanvasRef.current;
        if (image && canvas) {
            const { width, height } = image.getBoundingClientRect();
            canvas.style.width = `${width}px`;
            canvas.style.height = `${height}px`;
            canvas.width = image.naturalWidth;
            canvas.height = image.naturalHeight;
        }
    }, []);

    useEffect(() => {
        if (mode === 'edit' && editTab === 'ai' && maskingEnabled) {
            handleCanvasResize();
            const imageEl = imageRef.current;
            if (!imageEl) return;
            
            const resizeObserver = new ResizeObserver(handleCanvasResize);
            resizeObserver.observe(imageEl);
            
            return () => resizeObserver.disconnect();
        }
    }, [mode, editTab, maskingEnabled, generatedImage, handleCanvasResize]);

    // Drawing functions
    const getMousePos = (e) => {
        const canvas = drawingCanvasRef.current;
        if (!canvas) return null;
        const rect = canvas.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) * (canvas.width / rect.width),
            y: (e.clientY - rect.top) * (canvas.height / rect.height)
        };
    };

    const drawLine = (x0, y0, x1, y1) => {
        const ctx = drawingCanvasRef.current?.getContext('2d');
        if (!ctx) return;

        ctx.lineWidth = brushSize * (drawingCanvasRef.current.width / drawingCanvasRef.current.getBoundingClientRect().width);
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';

        if (drawingMode === 'brush') {
            ctx.strokeStyle = 'rgba(239, 68, 68, 0.7)';
            ctx.globalCompositeOperation = 'source-over';
        } else {
            ctx.strokeStyle = 'rgba(0, 0, 0, 1)';
            ctx.globalCompositeOperation = 'destination-out';
        }

        ctx.beginPath();
        ctx.moveTo(x0, y0);
        ctx.lineTo(x1, y1);
        ctx.stroke();
    };

    const handleMouseDown = (e) => {
        if (!maskingEnabled) return;
        const pos = getMousePos(e);
        if (pos) {
            setIsDrawing(true);
            lastPoint.current = pos;
        }
    };
    
    const handleMouseMove = (e) => {
        if (!isDrawing || !maskingEnabled) return;
        const pos = getMousePos(e);
        if (pos && lastPoint.current) {
            drawLine(lastPoint.current.x, lastPoint.current.y, pos.x, pos.y);
            lastPoint.current = pos;
        }
    };

    const handleMouseUp = () => {
        setIsDrawing(false);
        lastPoint.current = null;
    };

    const handleClearMask = () => {
        const canvas = drawingCanvasRef.current;
        if (canvas) {
            const ctx = canvas.getContext('2d');
            ctx?.clearRect(0, 0, canvas.width, canvas.height);
        }
    };

    // Crop functions
    const handleStartCrop = () => {
        const image = imageRef.current;
        if (!image) return;
        setMaskingEnabled(false);
        const { clientWidth, clientHeight } = image;
        const initialWidth = clientWidth * 0.8;
        const initialHeight = clientHeight * 0.8;
        setCrop({
            x: (clientWidth - initialWidth) / 2,
            y: (clientHeight - initialHeight) / 2,
            width: initialWidth,
            height: initialHeight,
        });
    };

    const handleCancelCrop = () => setCrop(null);

    const handleApplyCrop = () => {
        const image = imageRef.current;
        if (!image || !image.src || !crop) return;
        
        const canvas = document.createElement('canvas');
        const scaleX = image.naturalWidth / image.clientWidth;
        const scaleY = image.naturalHeight / image.clientHeight;

        const sourceX = crop.x * scaleX;
        const sourceY = crop.y * scaleY;
        const sourceWidth = crop.width * scaleX;
        const sourceHeight = crop.height * scaleY;

        canvas.width = sourceWidth;
        canvas.height = sourceHeight;

        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        
        ctx.drawImage(
            image,
            sourceX, sourceY, sourceWidth, sourceHeight,
            0, 0, sourceWidth, sourceHeight
        );
        
        addNewImageState(canvas.toDataURL('image/png'));
        setCrop(null);
    };
    
    const handleCropMouseDown = (e, handle) => {
        e.preventDefault();
        e.stopPropagation();
        if (!crop) return;
        setDragHandle(handle);
        setDragStart({ x: e.clientX, y: e.clientY, crop });
    };

    useEffect(() => {
        const handleMouseMoveWindow = (e) => {
            if (!dragStart || !crop || !dragHandle) return;
            const dx = e.clientX - dragStart.x;
            const dy = e.clientY - dragStart.y;
            
            let newCrop = { ...crop };
            
            if (dragHandle === 'move') {
                newCrop.x = dragStart.crop.x + dx;
                newCrop.y = dragStart.crop.y + dy;
            }
            if (dragHandle.includes('e')) newCrop.width = dragStart.crop.width + dx;
            if (dragHandle.includes('w')) {
                newCrop.width = dragStart.crop.width - dx;
                newCrop.x = dragStart.crop.x + dx;
            }
            if (dragHandle.includes('s')) newCrop.height = dragStart.crop.height + dy;
            if (dragHandle.includes('n')) {
                newCrop.height = dragStart.crop.height - dy;
                newCrop.y = dragStart.crop.y + dy;
            }

            if (newCrop.width < 20) {
                 if (dragHandle.includes('w')) newCrop.x = crop.x + crop.width - 20;
                 newCrop.width = 20;
            }
            if (newCrop.height < 20) {
                if (dragHandle.includes('n')) newCrop.y = crop.y + crop.height - 20;
                newCrop.height = 20;
            }
            setCrop(newCrop);
        };
        const handleMouseUpWindow = () => setDragStart(null);

        if (dragStart) {
            window.addEventListener('mousemove', handleMouseMoveWindow);
            window.addEventListener('mouseup', handleMouseUpWindow);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMoveWindow);
            window.removeEventListener('mouseup', handleMouseUpWindow);
        };
    }, [dragStart, crop, dragHandle]);

    // Apply filters and transformations
    const applyFilters = () => {
        if (!generatedImage) return;
        
        const img = new Image();
        img.onload = () => {
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            if (!ctx) return;
            
            canvas.width = img.width;
            canvas.height = img.height;
            
            // Apply transformations
            ctx.save();
            ctx.translate(canvas.width / 2, canvas.height / 2);
            ctx.rotate((rotation * Math.PI) / 180);
            ctx.scale(flipH ? -1 : 1, flipV ? -1 : 1);
            ctx.translate(-canvas.width / 2, -canvas.height / 2);
            
            // Apply filters
            ctx.filter = `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`;
            ctx.drawImage(img, 0, 0);
            ctx.restore();
            
            addNewImageState(canvas.toDataURL('image/png'));
            
            // Reset filter values
            setBrightness(100);
            setContrast(100);
            setSaturation(100);
            setRotation(0);
            setFlipH(false);
            setFlipV(false);
        };
        img.src = generatedImage;
    };

    // AI Edit handler
    const handleEditImage = async () => {
        if (!editPrompt?.trim()) {
            setError("Please describe what changes you want to make to the image.");
            return;
        }
        if (!generatedImage) {
            setError("No image to edit.");
            return;
        }
        // Credits are deducted by backend - removed upfront deduction

        setStatus('editing');
        setError(null);

        // Get mask if masking is enabled
        let maskToUse = undefined;
        if (maskingEnabled && drawingCanvasRef.current) {
            const canvas = drawingCanvasRef.current;
            const offscreenCanvas = document.createElement('canvas');
            offscreenCanvas.width = canvas.width;
            offscreenCanvas.height = canvas.height;
            const offscreenCtx = offscreenCanvas.getContext('2d');
            const drawingCtx = canvas.getContext('2d');

            if (offscreenCtx && drawingCtx) {
                const sourceImageData = drawingCtx.getImageData(0, 0, canvas.width, canvas.height);
                const sourceData = sourceImageData.data;

                const maskImageData = drawingCtx.createImageData(canvas.width, canvas.height);
                const maskData = maskImageData.data;
                let hasMaskContent = false;

                for (let i = 0; i < sourceData.length; i += 4) {
                    if (sourceData[i + 3] > 0) {
                        maskData[i] = 255; maskData[i + 1] = 255; maskData[i + 2] = 255; maskData[i + 3] = 255;
                        hasMaskContent = true;
                    } else {
                        maskData[i] = 0; maskData[i + 1] = 0; maskData[i + 2] = 0; maskData[i + 3] = 255;
                    }
                }

                if (hasMaskContent) {
                    offscreenCtx.putImageData(maskImageData, 0, 0);
                    maskToUse = offscreenCanvas.toDataURL('image/png');
                }
            }
        }

        try {
            console.log('🎨 Editing existing image with AI:');
            console.log('  - Edit Prompt:', editPrompt);
            console.log('  - Has Mask:', !!maskToUse);
            console.log('  - Reference Images:', referenceImages.length);
            
            const result = await editImage(
                generatedImage,
                editPrompt,
                [],
                referenceImages,
                maskToUse,
                'default'
            );
            
            console.log('🎨 Edit result:', result);
            
            // Get the edited image URL from various possible response formats
            const editedImage = result?.images?.[0] || result?.imageUrl || result?.image_url;
            
            if (editedImage) {
                console.log('✅ Got edited image:', editedImage.substring(0, 80) + '...');
                addNewImageState(editedImage);
                setEditPrompt('');
                handleClearMask();
                if (onSaveMedia) onSaveMedia([editedImage]);
                
                // Track credits from backend response
                if (onTrackCreditsUsed && (result?.creditsUsed || result?.credits_used)) {
                    onTrackCreditsUsed(result.creditsUsed || result.credits_used, result.creditsRemaining || result.credits_remaining, result.jobId || result.job_id);
                }
            } else {
                console.error('❌ No image in result:', result);
                throw new Error('No edited image returned from API');
            }
        } catch (err) {
            console.error('Image editing error:', err);
            setError(err.message || 'Failed to edit image');
        } finally {
            setStatus('idle');
        }
    };

    // Generate handler
    const handleGenerate = async () => {
        if (!prompt?.trim()) {
            setError("Please provide a description.");
            return;
        }
        // Credits are deducted by backend - removed upfront deduction

        setStatus('generating');
        setError(null);

        try {
            const category = character.category || 'character';
            const modelId = projectData?.modelPreferences?.image?.portrait?.textToImage || 
                           projectData?.modelPreferences?.image?.general?.textToImage || 
                           'default';
            
            console.log('🎨 Generating image:');
            console.log('  - Category:', category);
            console.log('  - Is Element:', isElement);
            console.log('  - Has reference images:', referenceImages.length);
            console.log('  - Has costume/accessory images:', costumeImages.length);
            console.log('  - Model:', modelId);
            console.log('  - Aspect Ratio:', projectAspectRatio);
            
            let image;
            
            if (isElement) {
                // Use dedicated element generation function for props, locations, objects
                // This doesn't use face/costume separation - just reference images
                console.log('🎭 Using generateElementImage for element');
                
                // Combine all reference images for elements
                const allElementRefs = [...referenceImages, ...costumeImages].filter(Boolean);
                console.log('  - Total element reference images:', allElementRefs.length);
                
                const result = await generateElementImage(
                    prompt,
                    allElementRefs,
                    projectAspectRatio,
                    modelId
                );
                
                // Extract image from result
                if (result.images && result.images.length > 0) {
                    image = result.images[0];
                } else if (result.imageUrl) {
                    image = result.imageUrl;
                }
            } else {
                // Use character portrait generation for characters and narrators
                // This uses face reference for likeness and costume for virtual try-on
                console.log('👤 Using generateCharacterPortraitWithGemini for character');
                console.log('  📝 Prompt:', prompt.substring(0, 200) + '...');
                console.log('  👤 Face reference images:', referenceImages.length);
                referenceImages.forEach((img, i) => console.log(`    Face ref ${i + 1}:`, img?.substring(0, 80) + '...'));
                console.log('  👗 Costume reference images:', costumeImages.length);
                costumeImages.forEach((img, i) => console.log(`    Costume ref ${i + 1}:`, img?.substring(0, 80) + '...'));
                
                // Validate reference images are proper data URLs or URLs
                const validFaceRefs = referenceImages.filter(img => 
                    img && (img.startsWith('data:') || img.startsWith('http') || img.startsWith('blob:'))
                );
                const validCostumeRefs = costumeImages.filter(img => 
                    img && (img.startsWith('data:') || img.startsWith('http') || img.startsWith('blob:'))
                );
                
                if (validFaceRefs.length !== referenceImages.length) {
                    console.warn('⚠️ Some face reference images were invalid and filtered out');
                }
                if (validCostumeRefs.length !== costumeImages.length) {
                    console.warn('⚠️ Some costume reference images were invalid and filtered out');
                }
                
                image = await generateCharacterPortraitWithGemini(
                    prompt,
                    validFaceRefs,
                    validCostumeRefs,
                    makeupLevel,
                    projectAspectRatio,
                    category,
                    modelId
                );
            }
            
            // Handle both old format (string) and new format (object with credits)
            const imageUrl = image?.image || image;
            const creditsUsed = image?.creditsUsed;
            const creditsRemaining = image?.creditsRemaining;
            const jobId = image?.jobId;
            
            if (imageUrl) {
                addNewImageState(imageUrl);
                if (onSaveMedia && imageUrl) onSaveMedia([imageUrl]);
                
                // Track credits from backend response
                if (onTrackCreditsUsed && creditsUsed) {
                    onTrackCreditsUsed(creditsUsed, creditsRemaining, jobId);
                }
            } else {
                throw new Error('No image generated');
            }
        } catch (err) {
            console.error('Image generation error:', err);
            setError(err.message || 'Generation failed');
        } finally {
            setStatus('idle');
        }
    };

    // Save handler
    const handleSave = () => {
        if (generatedImage) {
            const saveData = {
                name,
                description: prompt,
                image: generatedImage,
                category: character?.category || 'character', // Preserve category!
                likenessImages: referenceImages,
                costumeImages: costumeImages,
                makeupLevel,
                // Also save reference images for elements (for consistency across shots)
                referenceImages: referenceImages.length > 0 ? referenceImages : undefined,
                costumeAndAccessoryImages: costumeImages.length > 0 ? costumeImages : undefined
            };
            
            console.log('💾 Saving entity data:', {
                name,
                category: character?.category,
                hasImage: !!generatedImage,
                referenceImagesCount: referenceImages.length,
                costumeImagesCount: costumeImages.length
            });
            
            onSave(character.id, saveData);
            onClose();
        }
    };

    // Render crop handles
    const renderCropHandles = () => {
        const handles = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw', 'move'];
        return handles.map(handle => (
            <div
                key={handle}
                className={`absolute bg-purple-500/80 border border-white/50 ${handle === 'move' ? 'cursor-move inset-2' : 'cursor-pointer'}`}
                style={{
                    ...(handle.includes('n') && { top: -4, height: 8 }),
                    ...(handle.includes('s') && { bottom: -4, height: 8 }),
                    ...(handle.includes('e') && { right: -4, width: 8 }),
                    ...(handle.includes('w') && { left: -4, width: 8 }),
                    ...(!handle.includes('n') && !handle.includes('s') && { top: 0, bottom: 0 }),
                    ...(!handle.includes('e') && !handle.includes('w') && { left: 0, right: 0 }),
                }}
                onMouseDown={e => handleCropMouseDown(e, handle)}
            />
        ));
    };

    // Get filter style for preview
    const getFilterStyle = () => {
        if (mode !== 'edit' || editTab !== 'filters') return {};
        return {
            filter: `brightness(${brightness}%) contrast(${contrast}%) saturate(${saturation}%)`,
            transform: `rotate(${rotation}deg) scaleX(${flipH ? -1 : 1}) scaleY(${flipV ? -1 : 1})`
        };
    };

    return (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={onClose}>
            <div className="bg-[#181820] border border-gray-700 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="flex justify-between items-center p-6 border-b border-gray-700">
                    <div>
                        <h2 className="text-2xl font-bold">
                            {mode === 'edit' 
                                ? (isElement ? 'Edit Element Image' : isNarrator ? 'Edit Narrator Portrait' : 'Edit Character Portrait')
                                : mode === 'generate'
                                    ? (isElement ? 'Generate Element Image' : isNarrator ? 'Generate Narrator Portrait' : 'Generate Character Portrait')
                                    : (isElement ? 'Element Image' : isNarrator ? 'Narrator Portrait' : 'Character Portrait')
                            }
                        </h2>
                        <p className="text-sm text-gray-400 mt-1">
                            Aspect Ratio: <span className="text-purple-400 font-semibold">{projectAspectRatio}</span> (from project settings)
                            {isElement && <span className="ml-2 text-yellow-400">• No living things will be included</span>}
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {mode === 'edit' && (
                            <>
                                <button onClick={handleUndo} disabled={historyIndex <= 0} className="p-2 rounded-full hover:bg-gray-700 disabled:opacity-30" title="Undo">
                                    <UndoIcon className="w-5 h-5" />
                                </button>
                                <button onClick={handleRedo} disabled={historyIndex >= history.length - 1} className="p-2 rounded-full hover:bg-gray-700 disabled:opacity-30" title="Redo">
                                    <RedoIcon className="w-5 h-5" />
                                </button>
                            </>
                        )}
                        <button onClick={onClose} className="p-2 rounded-full hover:bg-gray-700">
                            <CloseIcon className="w-6 h-6" />
                        </button>
                    </div>
                </div>

                {/* Mode Tabs */}
                {hasExistingImage && (
                    <div className="flex border-b border-gray-700 px-6">
                        <button
                            onClick={() => setMode('view')}
                            className={`px-4 py-3 text-sm font-semibold transition-colors ${
                                mode === 'view' 
                                    ? 'text-purple-400 border-b-2 border-purple-400' 
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            View Image
                        </button>
                        <button
                            onClick={() => setMode('edit')}
                            className={`px-4 py-3 text-sm font-semibold transition-colors ${
                                mode === 'edit' 
                                    ? 'text-purple-400 border-b-2 border-purple-400' 
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            Edit Image
                        </button>
                        <button
                            onClick={() => setMode('generate')}
                            className={`px-4 py-3 text-sm font-semibold transition-colors ${
                                mode === 'generate' 
                                    ? 'text-purple-400 border-b-2 border-purple-400' 
                                    : 'text-gray-400 hover:text-white'
                            }`}
                        >
                            Generate New
                        </button>
                    </div>
                )}

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6">
                    
                    {/* VIEW MODE */}
                    {mode === 'view' && hasExistingImage && (
                        <div className="flex flex-col items-center gap-6">
                            <div className="relative max-w-2xl w-full">
                                <img 
                                    src={generatedImage || character.image} 
                                    alt={name}
                                    className="w-full rounded-lg border border-gray-700"
                                />
                            </div>
                            <div className="text-center">
                                <h3 className="text-xl font-semibold">{name}</h3>
                                <p className="text-gray-400 mt-1 max-w-xl">{prompt}</p>
                            </div>
                            <div className="flex gap-4">
                                <button
                                    onClick={() => setMode('edit')}
                                    className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <PencilIcon className="w-5 h-5" />
                                    Edit Image
                                </button>
                                <button
                                    onClick={() => setMode('generate')}
                                    className="px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <MagicWandIcon className="w-5 h-5" />
                                    Generate New
                                </button>
                                <button
                                    onClick={() => uploadNewImageRef.current?.click()}
                                    className="px-6 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
                                >
                                    <PlusIcon className="w-5 h-5" />
                                    Upload New
                                </button>
                                <input
                                    type="file"
                                    ref={uploadNewImageRef}
                                    accept="image/*"
                                    onChange={handleUploadNewImage}
                                    className="hidden"
                                />
                            </div>
                            <button
                                onClick={handleSave}
                                className="px-8 py-3 bg-green-600 hover:bg-green-700 text-white font-bold rounded-lg transition-colors"
                            >
                                Save & Close
                            </button>
                        </div>
                    )}

                    {/* EDIT MODE */}
                    {mode === 'edit' && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Left: Edit Controls */}
                            <div className="space-y-4">
                                {/* Edit Sub-tabs */}
                                <div className="flex gap-2 mb-4">
                                    <button
                                        onClick={() => { setEditTab('ai'); setCrop(null); }}
                                        className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                            editTab === 'ai' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        }`}
                                    >
                                        🤖 AI Edit
                                    </button>
                                    <button
                                        onClick={() => { setEditTab('basic'); setMaskingEnabled(false); }}
                                        className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                            editTab === 'basic' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        }`}
                                    >
                                        ✂️ Crop & Transform
                                    </button>
                                    <button
                                        onClick={() => { setEditTab('filters'); setCrop(null); setMaskingEnabled(false); }}
                                        className={`flex-1 py-2 rounded-lg text-sm font-semibold transition-colors ${
                                            editTab === 'filters' ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                        }`}
                                    >
                                        🎨 Filters
                                    </button>
                                </div>

                                {/* AI Edit Tab */}
                                {editTab === 'ai' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Edit Instructions</label>
                                            <textarea
                                                value={editPrompt}
                                                onChange={(e) => setEditPrompt(e.target.value)}
                                                rows={4}
                                                className="w-full p-3 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                                                placeholder="Describe what changes you want to make (e.g., 'Change the background to a beach sunset', 'Add a smile', 'Make the outfit red')..."
                                            />
                                        </div>

                                        {/* Masking Controls */}
                                        <div className="space-y-2">
                                            <button
                                                onClick={() => setMaskingEnabled(!maskingEnabled)}
                                                className={`w-full py-2 rounded-lg flex items-center justify-center gap-2 ${
                                                    maskingEnabled ? 'bg-purple-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                                }`}
                                            >
                                                <PencilIcon className="w-4 h-4" />
                                                {maskingEnabled ? 'Masking Active - Paint areas to edit' : 'Enable Masking (Inpainting)'}
                                            </button>
                                            
                                            {maskingEnabled && (
                                                <div className="bg-gray-800 p-3 rounded-lg space-y-3">
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => setDrawingMode('brush')}
                                                            className={`flex-1 py-2 rounded text-sm ${drawingMode === 'brush' ? 'bg-purple-600' : 'bg-gray-700'}`}
                                                        >
                                                            🖌️ Brush
                                                        </button>
                                                        <button
                                                            onClick={() => setDrawingMode('eraser')}
                                                            className={`flex-1 py-2 rounded text-sm ${drawingMode === 'eraser' ? 'bg-purple-600' : 'bg-gray-700'}`}
                                                        >
                                                            🧹 Eraser
                                                        </button>
                                                    </div>
                                                    <div>
                                                        <label className="text-xs text-gray-400">Brush Size: {brushSize}px</label>
                                                        <input
                                                            type="range"
                                                            min="10"
                                                            max="100"
                                                            value={brushSize}
                                                            onChange={(e) => setBrushSize(parseInt(e.target.value))}
                                                            className="w-full accent-purple-500"
                                                        />
                                                    </div>
                                                    <button 
                                                        onClick={handleClearMask} 
                                                        className="w-full py-1 bg-gray-700 hover:bg-gray-600 rounded text-sm"
                                                    >
                                                        Clear Mask
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Reference Images for Edit */}
                                        <div className="space-y-2">
                                            <label className="block text-sm font-medium">Reference Images (Optional)</label>
                                            <input
                                                type="file"
                                                ref={editReferenceInputRef}
                                                accept="image/*"
                                                multiple
                                                onChange={(e) => handleFileChange(e, setReferenceImages)}
                                                className="hidden"
                                            />
                                            <button
                                                onClick={() => editReferenceInputRef.current?.click()}
                                                className="w-full py-2 border border-dashed border-gray-600 rounded-lg hover:border-purple-500 flex items-center justify-center gap-2 text-sm"
                                            >
                                                <PlusIcon className="w-4 h-4" />
                                                Add Reference Image
                                            </button>
                                            {referenceImages.length > 0 && (
                                                <div className="flex flex-wrap gap-2">
                                                    {referenceImages.map((img, idx) => (
                                                        <div key={idx} className="relative w-16 h-16 group">
                                                            <img src={img} className="w-full h-full object-cover rounded" alt={`Ref ${idx + 1}`} />
                                                            <button
                                                                onClick={() => handleRemoveReference(idx, setReferenceImages)}
                                                                className="absolute -top-2 -right-2 bg-red-500 rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                                                            >
                                                                ×
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>

                                        <button
                                            onClick={handleEditImage}
                                            disabled={status !== 'idle' || !editPrompt.trim()}
                                            className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center"
                                        >
                                            {status === 'editing' ? (
                                                <><LoadingSpinner className="w-5 h-5 mr-2" /> Applying AI Edit...</>
                                            ) : (
                                                <>✨ Apply AI Edit</>
                                            )}
                                        </button>
                                    </div>
                                )}

                                {/* Basic Edit Tab (Crop & Transform) */}
                                {editTab === 'basic' && (
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-3">
                                            <button
                                                onClick={handleStartCrop}
                                                disabled={!!crop}
                                                className="py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-lg flex items-center justify-center gap-2"
                                            >
                                                <CropIcon className="w-5 h-5" />
                                                Crop
                                            </button>
                                            <button
                                                onClick={() => setRotation(r => (r + 90) % 360)}
                                                className="py-3 bg-gray-700 hover:bg-gray-600 rounded-lg"
                                            >
                                                🔄 Rotate 90°
                                            </button>
                                            <button
                                                onClick={() => setFlipH(!flipH)}
                                                className={`py-3 rounded-lg ${flipH ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                                            >
                                                ↔️ Flip Horizontal
                                            </button>
                                            <button
                                                onClick={() => setFlipV(!flipV)}
                                                className={`py-3 rounded-lg ${flipV ? 'bg-purple-600' : 'bg-gray-700 hover:bg-gray-600'}`}
                                            >
                                                ↕️ Flip Vertical
                                            </button>
                                        </div>

                                        {crop && (
                                            <div className="flex gap-2">
                                                <button 
                                                    onClick={handleApplyCrop} 
                                                    className="flex-1 bg-purple-600 hover:bg-purple-700 py-2 rounded-lg"
                                                >
                                                    ✓ Apply Crop
                                                </button>
                                                <button 
                                                    onClick={handleCancelCrop} 
                                                    className="flex-1 bg-gray-700 hover:bg-gray-600 py-2 rounded-lg"
                                                >
                                                    ✕ Cancel
                                                </button>
                                            </div>
                                        )}

                                        {(rotation !== 0 || flipH || flipV) && !crop && (
                                            <button
                                                onClick={applyFilters}
                                                className="w-full bg-green-600 hover:bg-green-700 py-3 rounded-lg font-semibold"
                                            >
                                                ✓ Apply Transformations
                                            </button>
                                        )}

                                        {/* Upload New Image */}
                                        <div className="pt-4 border-t border-gray-700">
                                            <label className="block text-sm font-medium mb-2">Replace Image</label>
                                            <button
                                                onClick={() => uploadNewImageRef.current?.click()}
                                                className="w-full py-3 border border-dashed border-gray-600 rounded-lg hover:border-purple-500 flex items-center justify-center gap-2"
                                            >
                                                <PlusIcon className="w-5 h-5" />
                                                Upload New Image
                                            </button>
                                            <input
                                                type="file"
                                                ref={uploadNewImageRef}
                                                accept="image/*"
                                                onChange={handleUploadNewImage}
                                                className="hidden"
                                            />
                                        </div>
                                    </div>
                                )}

                                {/* Filters Tab */}
                                {editTab === 'filters' && (
                                    <div className="space-y-4">
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Brightness: {brightness}%</label>
                                            <input
                                                type="range"
                                                min="50"
                                                max="150"
                                                value={brightness}
                                                onChange={(e) => setBrightness(parseInt(e.target.value))}
                                                className="w-full accent-purple-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Contrast: {contrast}%</label>
                                            <input
                                                type="range"
                                                min="50"
                                                max="150"
                                                value={contrast}
                                                onChange={(e) => setContrast(parseInt(e.target.value))}
                                                className="w-full accent-purple-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium mb-2">Saturation: {saturation}%</label>
                                            <input
                                                type="range"
                                                min="0"
                                                max="200"
                                                value={saturation}
                                                onChange={(e) => setSaturation(parseInt(e.target.value))}
                                                className="w-full accent-purple-500"
                                            />
                                        </div>

                                        {/* Quick Presets */}
                                        <div className="pt-4 border-t border-gray-700">
                                            <label className="block text-sm font-medium mb-2">Quick Presets</label>
                                            <div className="grid grid-cols-3 gap-2">
                                                <button
                                                    onClick={() => { setBrightness(100); setContrast(100); setSaturation(100); }}
                                                    className="py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm"
                                                >
                                                    Reset
                                                </button>
                                                <button
                                                    onClick={() => { setBrightness(105); setContrast(110); setSaturation(110); }}
                                                    className="py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm"
                                                >
                                                    Vivid
                                                </button>
                                                <button
                                                    onClick={() => { setBrightness(95); setContrast(105); setSaturation(0); }}
                                                    className="py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm"
                                                >
                                                    B&W
                                                </button>
                                                <button
                                                    onClick={() => { setBrightness(110); setContrast(95); setSaturation(90); }}
                                                    className="py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm"
                                                >
                                                    Soft
                                                </button>
                                                <button
                                                    onClick={() => { setBrightness(90); setContrast(120); setSaturation(80); }}
                                                    className="py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm"
                                                >
                                                    Dramatic
                                                </button>
                                                <button
                                                    onClick={() => { setBrightness(105); setContrast(95); setSaturation(120); }}
                                                    className="py-2 bg-gray-700 hover:bg-gray-600 rounded text-sm"
                                                >
                                                    Warm
                                                </button>
                                            </div>
                                        </div>

                                        {(brightness !== 100 || contrast !== 100 || saturation !== 100) && (
                                            <button
                                                onClick={applyFilters}
                                                className="w-full bg-green-600 hover:bg-green-700 py-3 rounded-lg font-semibold"
                                            >
                                                ✓ Apply Filters
                                            </button>
                                        )}
                                    </div>
                                )}

                                {error && (
                                    <div className="bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg text-sm">
                                        {error}
                                    </div>
                                )}
                            </div>

                            {/* Right: Preview */}
                            <div className="space-y-4">
                                <label className="block text-sm font-medium">Preview</label>
                                <div className="bg-gray-900 rounded-lg overflow-hidden flex items-center justify-center relative" style={{ minHeight: '400px' }}>
                                    {status === 'editing' ? (
                                        <div className="text-center">
                                            <LoadingSpinner className="w-12 h-12 mx-auto mb-4" />
                                            <p className="text-gray-400">Applying edits...</p>
                                        </div>
                                    ) : generatedImage ? (
                                        <div className="relative w-full h-full">
                                            <img 
                                                ref={imageRef}
                                                src={generatedImage} 
                                                alt={name}
                                                className="w-full h-full object-contain"
                                                style={getFilterStyle()}
                                            />
                                            {/* Mask Canvas - only show in AI edit mode with masking enabled */}
                                            {editTab === 'ai' && maskingEnabled && (
                                                <canvas
                                                    ref={drawingCanvasRef}
                                                    className="absolute top-0 left-0 pointer-events-auto cursor-crosshair"
                                                    style={{ mixBlendMode: 'multiply' }}
                                                    onMouseDown={handleMouseDown}
                                                    onMouseMove={handleMouseMove}
                                                    onMouseUp={handleMouseUp}
                                                    onMouseLeave={handleMouseUp}
                                                />
                                            )}
                                            {/* Crop overlay */}
                                            {crop && (
                                                <div
                                                    className="absolute border-2 border-purple-500"
                                                    style={{
                                                        left: crop.x,
                                                        top: crop.y,
                                                        width: crop.width,
                                                        height: crop.height,
                                                    }}
                                                >
                                                    {renderCropHandles()}
                                                </div>
                                            )}
                                        </div>
                                    ) : (
                                        <p className="text-gray-500">No image to edit</p>
                                    )}
                                </div>
                                {generatedImage !== character.image && historyIndex > 0 && (
                                    <p className="text-sm text-green-400 text-center">✓ Image has been modified ({historyIndex} edit{historyIndex > 1 ? 's' : ''})</p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* GENERATE MODE */}
                    {mode === 'generate' && (
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left: Controls */}
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium mb-2">
                                    {isElement ? 'Element Name' : isNarrator ? 'Narrator Name' : 'Character Name'}
                                </label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium mb-2">Description / Prompt</label>
                                <textarea
                                    value={prompt}
                                    onChange={(e) => setPrompt(e.target.value)}
                                    rows={4}
                                    className="w-full p-2 bg-gray-800 border border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
                                    placeholder={isElement 
                                        ? "Describe the element/object (e.g., 'A vintage red sports car', 'A cozy wooden cabin')..." 
                                        : "Describe the character's appearance..."}
                                />
                                {isElement && (
                                    <p className="text-xs text-yellow-400 mt-1">
                                        ℹ️ The generated image will NOT include any humans, animals, or living creatures.
                                    </p>
                                )}
                                {!isElement && prompt && (prompt.includes('In Scene') || prompt.includes('scene')) && (
                                    <p className="text-xs text-yellow-400 mt-1">
                                        ⚠️ <strong>Scene-specific text detected.</strong> For a consistent character portrait used throughout the project, edit the description to include only general physical features (face, hair, build, age) rather than scene-specific costumes.
                                    </p>
                                )}
                                {!isElement && referenceImages.length > 0 && (
                                    <p className="text-xs text-green-400 mt-1">
                                        ✅ Face reference image added - the portrait will maintain this likeness.
                                    </p>
                                )}
                                {!isElement && costumeImages.length > 0 && (
                                    <p className="text-xs text-green-400 mt-1">
                                        ✅ Costume reference image added - the portrait will use this outfit.
                                    </p>
                                )}
                            </div>

                            {/* Makeup Level - Only for characters/narrators */}
                            {!isElement && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Makeup Level</label>
                                    <div className="grid grid-cols-3 gap-2">
                                        {['natural', 'light', 'heavy'].map((level) => (
                                            <button
                                                key={level}
                                                type="button"
                                                onClick={() => setMakeupLevel(level)}
                                                className={`px-3 py-2 rounded-md text-sm font-semibold transition-all capitalize ${
                                                    makeupLevel === level ? 'bg-purple-600 text-white' : 'bg-gray-700/50 hover:bg-gray-600'
                                                }`}
                                            >
                                                {level}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Reference Images */}
                            {!isElement && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Reference Images (Face/Likeness)</label>
                                    <input
                                        type="file"
                                        ref={likenessFileInputRef}
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => handleFileChange(e, setReferenceImages)}
                                        className="hidden"
                                    />
                                    <div className="grid grid-cols-4 gap-2">
                                        {referenceImages.map((img, idx) => (
                                            <div key={idx} className="relative group">
                                                <img src={img} alt="" className="w-full h-20 object-cover rounded-lg" />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveReference(idx, setReferenceImages)}
                                                    className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <TrashIcon className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => likenessFileInputRef.current?.click()}
                                            className="w-full h-20 border-2 border-dashed border-gray-600 rounded-lg hover:border-purple-500 flex items-center justify-center"
                                        >
                                            <PlusIcon className="w-6 h-6 text-gray-500" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Costume Images - Only for characters/narrators */}
                            {!isElement && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Costume/Accessory Images</label>
                                    <input
                                        type="file"
                                        ref={costumeFileInputRef}
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => handleFileChange(e, setCostumeImages)}
                                        className="hidden"
                                    />
                                    <div className="grid grid-cols-4 gap-2">
                                        {costumeImages.map((img, idx) => (
                                            <div key={idx} className="relative group">
                                                <img src={img} alt="" className="w-full h-20 object-cover rounded-lg" />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveReference(idx, setCostumeImages)}
                                                    className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <TrashIcon className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => costumeFileInputRef.current?.click()}
                                            className="w-full h-20 border-2 border-dashed border-gray-600 rounded-lg hover:border-purple-500 flex items-center justify-center"
                                        >
                                            <PlusIcon className="w-6 h-6 text-gray-500" />
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Element Reference Images */}
                            {isElement && (
                                <div>
                                    <label className="block text-sm font-medium mb-2">Reference Images (Optional)</label>
                                    <input
                                        type="file"
                                        ref={likenessFileInputRef}
                                        accept="image/*"
                                        multiple
                                        onChange={(e) => handleFileChange(e, setReferenceImages)}
                                        className="hidden"
                                    />
                                    <div className="grid grid-cols-4 gap-2">
                                        {referenceImages.map((img, idx) => (
                                            <div key={idx} className="relative group">
                                                <img src={img} alt="" className="w-full h-20 object-cover rounded-lg" />
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveReference(idx, setReferenceImages)}
                                                    className="absolute top-1 right-1 p-1 bg-red-500 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <TrashIcon className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                        <button
                                            type="button"
                                            onClick={() => likenessFileInputRef.current?.click()}
                                            className="w-full h-20 border-2 border-dashed border-gray-600 rounded-lg hover:border-purple-500 flex items-center justify-center"
                                        >
                                            <PlusIcon className="w-6 h-6 text-gray-500" />
                                        </button>
                                    </div>
                                    <p className="text-xs text-gray-400 mt-1">
                                        Add reference images of similar objects/elements for style guidance.
                                    </p>
                                </div>
                            )}

                            {error && (
                                <div className="bg-red-500/20 border border-red-500 text-red-300 p-3 rounded-lg text-sm">
                                    {error}
                                </div>
                            )}

                            <button
                                onClick={handleGenerate}
                                disabled={status !== 'idle' || !prompt.trim()}
                                className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-bold py-3 rounded-lg transition-colors flex items-center justify-center"
                            >
                                {status === 'generating' ? (
                                    <><LoadingSpinner className="w-5 h-5 mr-2" /> Generating...</>
                                ) : (
                                    <><MagicWandIcon className="w-5 h-5 mr-2" /> {isElement ? 'Generate Element' : 'Generate Portrait'}</>
                                )}
                            </button>
                        </div>

                        {/* Right: Preview */}
                        <div>
                            <label className="block text-sm font-medium mb-2">Preview</label>
                            <div className="bg-gray-900 rounded-lg aspect-square flex items-center justify-center">
                                {generatedImage ? (
                                    <img src={generatedImage} alt={name} className="w-full h-full object-contain rounded-lg" />
                                ) : (
                                    <p className="text-gray-500">No image generated yet</p>
                                )}
                            </div>
                        </div>
                    </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-4 p-6 border-t border-gray-700">
                    <button
                        onClick={onClose}
                        className="bg-gray-700 hover:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={!generatedImage}
                        className="bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 text-white font-bold py-2 px-6 rounded-lg"
                    >
                        Save Portrait
                    </button>
                </div>
            </div>
        </div>
    );
};
